import React, { useRef, useState } from 'react';
import axios from 'axios';
import { UploadCloud, Loader2, Trash2, ArrowLeft, ArrowRight, Images } from 'lucide-react';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { resolveMediaUrl } from '../../lib/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Multi-image uploader for photo galleries.
 * Accepts several files at once (drag & drop or picker), uploads them in sequence and
 * returns the full ordered list of URLs. Individual images can be reordered or removed.
 */
const MultiImageDropUpload = ({
  label = 'Gallery',
  hint = 'drop several images at once',
  value = [],
  onChange,
  folder,
  maxMb = 20,
  maxImages = 24,
  testId,
}) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const inputRef = useRef(null);

  const images = Array.isArray(value) ? value : [];

  const uploadMany = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) {
      toast({ title: 'No images', description: 'Please choose image files.', variant: 'destructive' });
      return;
    }
    const room = maxImages - images.length;
    if (room <= 0) {
      toast({ title: 'Gallery full', description: `Maximum ${maxImages} images.`, variant: 'destructive' });
      return;
    }
    const batch = files.slice(0, room);

    setUploading(true);
    setProgress({ done: 0, total: batch.length });
    const uploaded = [];
    for (let i = 0; i < batch.length; i += 1) {
      const file = batch[i];
      if (file.size > maxMb * 1024 * 1024) {
        toast({ title: 'Skipped a file', description: `${file.name} is over ${maxMb}MB.`, variant: 'destructive' });
        setProgress({ done: i + 1, total: batch.length });
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        const { data } = await axios.post(`${API}/storage/upload`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        if (data?.url) uploaded.push(data.url);
      } catch (error) {
        toast({
          title: 'Upload failed',
          description: `${file.name}: ${error.response?.data?.detail || 'could not upload'}`,
          variant: 'destructive',
        });
      }
      setProgress({ done: i + 1, total: batch.length });
    }
    setUploading(false);

    if (uploaded.length) {
      onChange([...images, ...uploaded]);
      toast({ title: 'Uploaded', description: `${uploaded.length} image(s) added. Remember to save.` });
    }
  };

  const removeAt = (index) => onChange(images.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const next = [...images];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Images className="w-4 h-4 text-blue-600" />
          {label} ({images.length})
        </Label>
        {images.length > 0 && (
          <button type="button" onClick={() => onChange([])}
            className="text-sm text-red-600 hover:text-red-700">
            Clear all
          </button>
        )}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); uploadMany(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`mt-1 rounded-xl border-2 border-dashed cursor-pointer px-4 py-6 text-center transition-colors ${
          dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400'
        }`}
        data-testid={testId ? `${testId}-dropzone` : undefined}
      >
        {uploading ? (
          <div className="text-gray-600">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-blue-600" />
            <p className="text-sm mt-2">Uploading {progress.done} / {progress.total}...</p>
          </div>
        ) : (
          <div className="text-gray-500">
            <UploadCloud className="w-7 h-7 mx-auto mb-2 text-gray-400" />
            <p className="text-sm font-medium">Drag &amp; drop or click to add photos</p>
            <p className="text-xs mt-1">{hint} &middot; up to {maxImages}</p>
          </div>
        )}
      </div>

      <input
        type="file"
        accept="image/*"
        multiple
        ref={inputRef}
        className="hidden"
        onChange={(e) => uploadMany(e.target.files)}
      />

      {images.length > 0 && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
          {images.map((url, index) => (
            <div key={`${url}-${index}`} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
              <img src={resolveMediaUrl(url)} alt={`Gallery ${index + 1}`} className="w-full h-20 object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <button type="button" onClick={() => move(index, -1)} className="p-1 text-white hover:text-blue-200" title="Move left">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => removeAt(index)} className="p-1 text-white hover:text-red-300" title="Remove"
                  data-testid={testId ? `${testId}-remove-${index + 1}` : undefined}>
                  <Trash2 className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => move(index, 1)} className="p-1 text-white hover:text-blue-200" title="Move right">
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiImageDropUpload;
